import { useState, FormEvent } from 'react';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Everything Automation',
  description: 'Get in touch with Everything Automation. Book a free AI automation demo or send us a message. We typically respond within 2 hours.',
  url: `${siteUrl}/contact`,
  publisher: {
    '@type': 'Organization',
    name: 'Everything Automation',
    url: siteUrl,
    telephone: '+44-7450-946360',
    email: 'sales@everythingautomation.co',
  },
};

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d6tbm3vcrcujsk0v16mg';
const DEMO_FORM_URL = 'https://readdy.ai/api/form/d6tbm57crcujsk0v16n0';
const DEMO_BOOKING_HANDLER_URL = 'https://rvajmknywjpopbjfjagt.supabase.co/functions/v1/demo-booking-handler';
const N8N_WEBHOOK_URL = 'https://n8n.everythingautomation.co/webhook/demo-booking';

const INDUSTRIES = [
  'Hair & Beauty Salon',
  'Dental / Medical Clinic',
  'Builder / Trades',
  'Fitness & Wellness',
  'Home Services (Plumber, Cleaner, Electrician)',
  'Physio & Therapy',
  'E-Commerce / Retail',
  'Real Estate / Lettings',
  'Education / Coaching',
  'Other / Not Listed',
];

const COMPANY_SIZES = [
  'Just Me (Sole Trader)',
  '2–5 Employees',
  '6–15 Employees',
  '16–50 Employees',
  '51–100 Employees',
  '101–250 Employees',
  '251–500 Employees',
  '501–1,000 Employees',
  '1,000+ Employees',
];

const PLAN_OPTIONS = [
  'Starter (£149/mo) - Automated Lead Capture System',
  'Pro (£299/mo) - Automated Sales & Booking System',
  'Scale (£599/mo) - Fully Automated Business Operating System',
  'Custom Pricing - Fully Tailored Automation System',
  'Not sure yet, need guidance',
];

const CLIENT_VOLUMES = [
  'Under 10 clients/jobs per week',
  '10 to 30 clients/jobs per week',
  '30 to 60 clients/jobs per week',
  '60+ clients/jobs per week',
];

const ENQUIRY_FREQUENCIES = [
  'Multiple times daily',
  'Once or twice a day',
  'A few times a week',
  'A few times a month',
];

const INTERESTS = [
  { id: 'ai-chatbot', label: 'AI Chatbot', icon: 'ri-robot-2-line' },
  { id: 'crm-automation', label: 'CRM Automation', icon: 'ri-contacts-line' },
  { id: 'lead-capture', label: 'Lead Capture', icon: 'ri-user-add-line' },
  { id: 'booking-systems', label: 'Booking Systems', icon: 'ri-calendar-check-line' },
  { id: 'email-automation', label: 'Email Automation', icon: 'ri-mail-send-line' },
  { id: 'whatsapp-automation', label: 'WhatsApp Automation', icon: 'ri-whatsapp-line' },
  { id: 'review-automation', label: 'Review Requests', icon: 'ri-star-line' },
  { id: 'website-build', label: 'Website Build', icon: 'ri-global-line' },
];

const CHANNELS = [
  { icon: 'ri-mail-line', label: 'Email', color: 'text-sky-400' },
  { icon: 'ri-whatsapp-line', label: 'WhatsApp', color: 'text-green-400' },
  { icon: 'ri-instagram-line', label: 'Instagram DMs', color: 'text-pink-400' },
  { icon: 'ri-facebook-circle-line', label: 'Facebook Messenger', color: 'text-blue-400' },
  { icon: 'ri-message-2-line', label: 'SMS / Text', color: 'text-yellow-400' },
  { icon: 'ri-linkedin-line', label: 'LinkedIn', color: 'text-sky-300' },
  { icon: 'ri-tiktok-line', label: 'TikTok', color: 'text-white' },
  { icon: 'ri-global-line', label: 'Website Chat', color: 'text-ea-blue' },
];

const ContactPage = () => {
  const [contactStatus, setContactStatus] = useState<FormStatus>('idle');
  const [demoStatus, setDemoStatus] = useState<FormStatus>('idle');

  // Demo form state
  const [demoName, setDemoName] = useState('');
  const [demoEmail, setDemoEmail] = useState('');
  const [demoPhone, setDemoPhone] = useState('');
  const [demoBusiness, setDemoBusiness] = useState('');
  const [demoIndustry, setDemoIndustry] = useState('');
  const [demoCompanySize, setDemoCompanySize] = useState('');
  const [demoInterests, setDemoInterests] = useState<string[]>([]);
  const [demoMessage, setDemoMessage] = useState('');
  const [demoPlan, setDemoPlan] = useState('');
  const [demoClientVolume, setDemoClientVolume] = useState('');
  const [demoEnquiryFreq, setDemoEnquiryFreq] = useState('');

  // Contact form state
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactBusiness, setContactBusiness] = useState('');
  const [contactMessage, setContactMessage] = useState('');

  const toggleInterest = (id: string) => {
    setDemoInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) return;
    if (contactMessage.length > 500) return;
    setContactStatus('submitting');
    try {
      const submittedAt = new Date().toISOString();
      const body = new URLSearchParams({
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
        business: contactBusiness,
        message: contactMessage,
      });
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        // Fire n8n webhook notification (non-blocking)
        fetch(N8N_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: contactName,
            email: contactEmail,
            phone: contactPhone,
            businessName: contactBusiness,
            message: contactMessage,
            submittedAt,
            sourcePage: '/contact',
            formType: 'contact_message',
          }),
        }).catch(() => {/* non-blocking */});

        setContactStatus('success');
        setContactName('');
        setContactEmail('');
        setContactPhone('');
        setContactBusiness('');
        setContactMessage('');
      } else {
        setContactStatus('error');
      }
    } catch {
      setContactStatus('error');
    }
  };

  const handleDemoSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!demoName.trim() || !demoEmail.trim() || !demoBusiness.trim() || !demoIndustry) return;
    if (demoMessage.length > 500) return;
    setDemoStatus('submitting');
    try {
      const body = new URLSearchParams({
        name: demoName,
        email: demoEmail,
        phone: demoPhone,
        business: demoBusiness,
        industry: demoIndustry,
        company_size: demoCompanySize,
        client_volume: demoClientVolume,
        enquiry_frequency: demoEnquiryFreq,
        plan_interest: demoPlan,
        interests: demoInterests.join(', '),
        message: demoMessage,
      });
      const res = await fetch(DEMO_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        // Trigger automated email sequence
        fetch(DEMO_BOOKING_HANDLER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: demoName,
            email: demoEmail,
            phone: demoPhone,
            business: demoBusiness,
            industry: demoIndustry,
            company_size: demoCompanySize,
            client_volume: demoClientVolume,
            enquiry_frequency: demoEnquiryFreq,
            plan_interest: demoPlan,
            interests: demoInterests.join(', '),
            message: demoMessage,
            source: 'contact_page',
          }),
        }).catch(() => {/* non-blocking */});

        setDemoStatus('success');
        setDemoName('');
        setDemoEmail('');
        setDemoPhone('');
        setDemoBusiness('');
        setDemoIndustry('');
        setDemoCompanySize('');
        setDemoInterests([]);
        setDemoMessage('');
        setDemoPlan('');
        setDemoClientVolume('');
        setDemoEnquiryFreq('');
      } else {
        setDemoStatus('error');
      }
    } catch {
      setDemoStatus('error');
    }
  };

  return (
    <>
      <title>Contact Everything Automation | Book a Free AI Automation Demo</title>
      <meta name="description" content="Book a free 30-minute demo or send a message to Everything Automation. We help UK businesses automate bookings, enquiries, and customer communication. Reply within 2 hours." />
      <meta name="keywords" content="contact Everything Automation, book AI automation demo, AI booking demo UK, business automation consultation" />
      <link rel="canonical" href={`${siteUrl}/contact`} />
      <meta property="og:title" content="Contact Everything Automation | Book a Free AI Automation Demo" />
      <meta property="og:description" content="Book a free 30-minute demo or message our team. We help UK businesses automate bookings, enquiries, and customer communication." />
      <meta property="og:url" content={`${siteUrl}/contact`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactPageSchema) }} />
      <main className="bg-ea-black">
        {/* Hero */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/269719f082764b421d994d458c5f0cf8.jpg"
              alt=""
              className="w-full h-full object-cover object-top opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-ea-black/50 to-ea-black"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
              <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Let&apos;s Talk</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-5">
              Let&apos;s Talk{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Automation
              </span>
            </h1>
            <p className="text-ea-gray text-lg max-w-2xl mx-auto">
              Book a demo and see how AI can transform your business in 30 minutes. We typically respond within 2 hours.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Left Info Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Info Card */}
                <div className="bg-gradient-to-br from-ea-blue-dark to-ea-navy rounded-xl p-8 border border-ea-blue/20">
                  <h2 className="text-white font-bold text-2xl mb-4">Book Your Free Demo</h2>
                  <p className="text-white/70 text-sm leading-relaxed mb-7">
                    In 30 minutes, we&apos;ll show you exactly how AI automation applies to your business, including a live walkthrough of the chatbot, booking system, and CRM.
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      { icon: 'ri-time-line', label: 'Fast Response', value: 'We reply within 2 hours' },
                      { icon: 'ri-calendar-check-line', label: 'Free Strategy Call', value: '30-minute Zoom session' },
                      { icon: 'ri-shield-check-line', label: 'No Commitment', value: 'No pressure, no contracts' },
                      { icon: 'ri-mail-line', label: 'Email Us', value: 'sales@everythingautomation.co' },
                      { icon: 'ri-whatsapp-line', label: 'WhatsApp Us', value: '+44 7450 946360', link: 'https://wa.me/447450946360' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0 mt-0.5">
                          <i className={`${item.icon} text-white text-base`}></i>
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{item.label}</p>
                          {item.link ? (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="nofollow noreferrer"
                              className="text-white/60 text-xs hover:text-white transition-colors cursor-pointer"
                            >
                              {item.value}
                            </a>
                          ) : (
                            <p className="text-white/60 text-xs">{item.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Channels strip */}
                  <div className="border-t border-white/10 pt-6">
                    <p className="text-white/50 text-xs uppercase tracking-wider font-medium mb-4">Our AI automates every channel</p>
                    <div className="grid grid-cols-4 gap-2">
                      {CHANNELS.map((ch) => (
                        <div key={ch.label} className="flex flex-col items-center gap-1.5 bg-white/5 rounded-lg py-3 px-1">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${ch.icon} ${ch.color} text-base`}></i>
                          </div>
                          <span className="text-white/50 text-center leading-tight" style={{ fontSize: '9px' }}>{ch.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-white/40 text-xs mt-3 leading-relaxed">
                      Whether your customers reach out via Instagram DMs, Facebook, WhatsApp, SMS, or your website, we automate it all.
                    </p>
                  </div>
                </div>

                {/* Response badge */}
                <div className="flex items-center gap-3 bg-ea-dark-card border border-ea-dark-border rounded-xl px-5 py-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse flex-shrink-0"></span>
                  <div>
                    <p className="text-white font-medium text-sm">Typically responds in under 2 hours</p>
                    <p className="text-ea-gray text-xs">Mon–Fri, 9am–7pm UK time</p>
                  </div>
                </div>

                {/* What Happens Next */}
                <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20">
                      <i className="ri-map-pin-time-line text-ea-blue text-sm"></i>
                    </div>
                    <p className="text-white font-bold text-sm">What Happens After You Book</p>
                  </div>
                  <div className="space-y-0">
                    {[
                      {
                        step: '01',
                        icon: 'ri-calendar-check-line',
                        title: 'We confirm within 2 hours',
                        desc: 'You\'ll hear from us fast to lock in your demo call.',
                        color: 'bg-ea-blue',
                      },
                      {
                        step: '02',
                        icon: 'ri-video-chat-line',
                        title: '30-min demo call',
                        desc: 'We walk through your business and show you exactly what\'s possible.',
                        color: 'bg-ea-blue',
                      },
                      {
                        step: '03',
                        icon: 'ri-tools-line',
                        title: 'Build begins (30–60 days)',
                        desc: 'We design, configure, and test your full automation system. Simpler builds go live in ~30 days. Complex systems take up to 60 days.',
                        color: 'bg-ea-blue',
                      },
                      {
                        step: '04',
                        icon: 'ri-rocket-line',
                        title: 'You go live',
                        desc: 'Your system is live, tested, and running 24/7. We monitor and optimise it every month.',
                        color: 'bg-green-500',
                      },
                    ].map((item, i, arr) => (
                      <div key={item.step} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-7 h-7 rounded-full ${item.color} flex items-center justify-center flex-shrink-0`}>
                            <i className={`${item.icon} text-white text-xs`}></i>
                          </div>
                          {i < arr.length - 1 && (
                            <div className="w-px flex-1 bg-ea-dark-border my-1.5 min-h-4"></div>
                          )}
                        </div>
                        <div className={`${i < arr.length - 1 ? 'pb-4' : ''}`}>
                          <p className="text-white font-semibold text-xs">{item.title}</p>
                          <p className="text-ea-gray text-xs leading-relaxed mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Forms Column */}
              <div className="lg:col-span-3 space-y-8">
                {/* Book a Demo Form */}
                <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-8"
                  style={{ boxShadow: '0 0 40px rgba(14, 165, 233, 0.07)' }}>
                  <h3 className="text-white font-bold text-xl mb-1">Book a Demo</h3>
                  <p className="text-ea-gray text-sm mb-6">Fill in your details and we&apos;ll be in touch within 2 hours to confirm your session.</p>

                  {demoStatus === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-4">
                        <i className="ri-check-line text-ea-blue text-2xl"></i>
                      </div>
                      <h4 className="text-white font-bold text-lg mb-2">Demo Booked!</h4>
                      <p className="text-ea-gray text-sm mb-5">We will be in touch within 2 hours to confirm your session. Check your email!</p>
                      <div className="w-full bg-ea-blue/5 border border-ea-blue/20 rounded-xl p-5 text-left space-y-3">
                        <p className="text-ea-blue font-bold text-xs uppercase tracking-wider">What Happens Next</p>
                        {[
                          { icon: 'ri-mail-check-line', title: 'Confirmation within 2 hours', desc: 'We will confirm your demo call time and send you a calendar invite.' },
                          { icon: 'ri-video-chat-line', title: '30-minute demo call', desc: 'We walk through your business and show you exactly what is possible with automation.' },
                          { icon: 'ri-tools-line', title: 'Build begins: 30 to 60 days', desc: 'Once you are onboarded, we design and build your full system. Simpler setups go live around 30 days. More complex builds take up to 60 days.' },
                          { icon: 'ri-rocket-line', title: 'You go live', desc: 'Your system runs 24/7 from day one. We monitor and optimise it every month.' },
                        ].map((step) => (
                          <div key={step.title} className="flex items-start gap-3">
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 shrink-0 mt-0.5">
                              <i className={`${step.icon} text-ea-blue text-xs`}></i>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-xs">{step.title}</p>
                              <p className="text-ea-gray text-xs leading-relaxed">{step.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <form
                      data-readdy-form
                      id="book-demo-form"
                      onSubmit={handleDemoSubmit}
                      className="space-y-5"
                    >
                      {/* Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={demoName}
                            onChange={(e) => setDemoName(e.target.value)}
                            required
                            placeholder="John Smith"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={demoEmail}
                            onChange={(e) => setDemoEmail(e.target.value)}
                            required
                            placeholder="john@yourbusiness.com"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                      </div>

                      {/* Phone + Business */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Phone Number</label>
                          <input
                            type="tel"
                            name="phone"
                            value={demoPhone}
                            onChange={(e) => setDemoPhone(e.target.value)}
                            placeholder="+44 7700 000000"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Business Name *</label>
                          <input
                            type="text"
                            name="business"
                            value={demoBusiness}
                            onChange={(e) => setDemoBusiness(e.target.value)}
                            required
                            placeholder="Your Business Name"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                      </div>

                      {/* Industry + Company Size */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Business Type *</label>
                          <div className="relative">
                            <select
                              name="industry"
                              value={demoIndustry}
                              onChange={(e) => setDemoIndustry(e.target.value)}
                              required
                              className="w-full appearance-none bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer pr-10"
                              style={{ color: demoIndustry ? 'white' : 'rgba(148,163,184,0.4)' }}
                            >
                              <option value="" disabled>Select industry...</option>
                              {INDUSTRIES.map((ind) => (
                                <option key={ind} value={ind} className="bg-ea-dark text-white">{ind}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                              <i className="ri-arrow-down-s-line text-ea-gray text-base"></i>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Company Size</label>
                          <div className="relative">
                            <select
                              name="company_size"
                              value={demoCompanySize}
                              onChange={(e) => setDemoCompanySize(e.target.value)}
                              className="w-full appearance-none bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer pr-10"
                              style={{ color: demoCompanySize ? 'white' : 'rgba(148,163,184,0.4)' }}
                            >
                              <option value="" disabled>Select size...</option>
                              {COMPANY_SIZES.map((s) => (
                                <option key={s} value={s} className="bg-ea-dark text-white">{s}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                              <i className="ri-arrow-down-s-line text-ea-gray text-base"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Client Volume + Enquiry Frequency */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Weekly Clients / Jobs</label>
                          <div className="relative">
                            <select
                              name="client_volume"
                              value={demoClientVolume}
                              onChange={(e) => setDemoClientVolume(e.target.value)}
                              className="w-full appearance-none bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer pr-10"
                              style={{ color: demoClientVolume ? 'white' : 'rgba(148,163,184,0.4)' }}
                            >
                              <option value="" disabled>How many per week?</option>
                              {CLIENT_VOLUMES.map((v) => (
                                <option key={v} value={v} className="bg-ea-dark text-white">{v}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                              <i className="ri-arrow-down-s-line text-ea-gray text-base"></i>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">How Often Do Enquiries Come In?</label>
                          <div className="relative">
                            <select
                              name="enquiry_frequency"
                              value={demoEnquiryFreq}
                              onChange={(e) => setDemoEnquiryFreq(e.target.value)}
                              className="w-full appearance-none bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer pr-10"
                              style={{ color: demoEnquiryFreq ? 'white' : 'rgba(148,163,184,0.4)' }}
                            >
                              <option value="" disabled>Select frequency...</option>
                              {ENQUIRY_FREQUENCIES.map((f) => (
                                <option key={f} value={f} className="bg-ea-dark text-white">{f}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                              <i className="ri-arrow-down-s-line text-ea-gray text-base"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Plan Interest */}
                      <div>
                        <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Which plan are you interested in?</label>
                        <div className="relative">
                          <select
                            name="plan_interest"
                            value={demoPlan}
                            onChange={(e) => setDemoPlan(e.target.value)}
                            className="w-full appearance-none bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer pr-10"
                            style={{ color: demoPlan ? 'white' : 'rgba(148,163,184,0.4)' }}
                          >
                            <option value="" disabled>Select a plan...</option>
                            {PLAN_OPTIONS.map((p) => (
                              <option key={p} value={p} className="bg-ea-dark text-white">{p}</option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line text-ea-gray text-base"></i>
                          </div>
                        </div>
                      </div>

                      {/* Interests */}
                      <div>
                        <label className="block text-ea-gray text-xs font-medium mb-2 uppercase tracking-wider">What are you interested in? <span className="normal-case tracking-normal text-ea-gray/40">(Select all that apply)</span></label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {INTERESTS.map((interest) => {
                            const selected = demoInterests.includes(interest.id);
                            return (
                              <button
                                key={interest.id}
                                type="button"
                                onClick={() => toggleInterest(interest.id)}
                                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all duration-200 cursor-pointer ${
                                  selected
                                    ? 'bg-ea-blue/15 border-ea-blue/50 text-white'
                                    : 'bg-ea-dark border-ea-dark-border text-ea-gray hover:border-ea-blue/30 hover:text-white'
                                }`}
                              >
                                <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                                  <i className={`${interest.icon} text-xs ${selected ? 'text-ea-blue' : 'text-ea-gray'}`}></i>
                                </div>
                                <span className="truncate">{interest.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">
                          Anything else we should know?
                          <span className="ml-2 text-ea-gray/50 normal-case tracking-normal">({demoMessage.length}/500)</span>
                        </label>
                        <textarea
                          name="message"
                          value={demoMessage}
                          onChange={(e) => {
                            if (e.target.value.length <= 500) setDemoMessage(e.target.value);
                          }}
                          rows={3}
                          placeholder="Tell us about your current setup, any goals, challenges, or anything that would help us prepare..."
                          className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors resize-none"
                        />
                      </div>

                      {demoStatus === 'error' && (
                        <p className="text-red-400 text-xs">Something went wrong. Please try again or email us directly at sales@everythingautomation.co</p>
                      )}

                      <button
                        type="submit"
                        disabled={demoStatus === 'submitting'}
                        className="w-full flex items-center justify-center gap-2 py-3.5 bg-ea-blue hover:bg-ea-blue-light text-white font-bold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70"
                        style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.3)' }}
                      >
                        {demoStatus === 'submitting' ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                        ) : (
                          <>Book a Free Demo <i className="ri-arrow-right-line"></i></>
                        )}
                      </button>
                      <p className="text-ea-gray/50 text-xs text-center">No account required &bull; We&apos;ll respond within 2 hours during business hours.</p>
                    </form>
                  )}
                </div>

                {/* General Contact Form */}
                <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-8">
                  <h3 className="text-white font-bold text-xl mb-1">Send a Message</h3>
                  <p className="text-ea-gray text-sm mb-6">Have a quick question? Drop us a message and we&apos;ll get back to you fast.</p>

                  {contactStatus === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-4">
                        <i className="ri-check-line text-ea-blue text-2xl"></i>
                      </div>
                      <h4 className="text-white font-bold text-lg mb-2">Message Sent!</h4>
                      <p className="text-ea-gray text-sm">Thanks for reaching out. We&apos;ll reply within 2 hours.</p>
                    </div>
                  ) : (
                    <form
                      data-readdy-form
                      id="contact-form"
                      onSubmit={handleContactSubmit}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            required
                            placeholder="Your full name"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            required
                            placeholder="your@email.com"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Phone Number</label>
                          <input
                            type="tel"
                            name="phone"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="+44 7700 000000"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">Business Name</label>
                          <input
                            type="text"
                            name="business"
                            value={contactBusiness}
                            onChange={(e) => setContactBusiness(e.target.value)}
                            placeholder="Your business (optional)"
                            className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wider">
                          Message *
                          <span className="ml-2 text-ea-gray/50 normal-case tracking-normal">({contactMessage.length}/500)</span>
                        </label>
                        <textarea
                          name="message"
                          value={contactMessage}
                          onChange={(e) => {
                            if (e.target.value.length <= 500) setContactMessage(e.target.value);
                          }}
                          rows={4}
                          required
                          placeholder="How can we help?"
                          className="w-full bg-ea-dark border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-2.5 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors resize-none"
                        />
                      </div>
                      {contactStatus === 'error' && (
                        <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                      )}
                      <button
                        type="submit"
                        disabled={contactStatus === 'submitting'}
                        className="w-full flex items-center justify-center gap-2 py-3.5 border border-ea-blue/50 hover:border-ea-blue text-ea-blue hover:text-ea-blue-light font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70"
                      >
                        {contactStatus === 'submitting' ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                        ) : (
                          <>Send Message <i className="ri-send-plane-line"></i></>
                        )}
                      </button>
                      <p className="text-ea-gray/50 text-xs text-center">No account required &bull; We reply within 2 hours.</p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ContactPage;
